import { useQuery, useQueryClient } from '@tanstack/react-query'
import Page from '../../components/layouts/page'
import { useParams } from 'react-router-dom'
import useStore from '../../hooks/use_store'
import { Input, Select, Toggle, FileUpload } from '@bitcine/cinesend-theme'
import useAuth from '../../hooks/use_auth.jsx'
import { useEffect, useState } from 'react'
import { useSnackbar } from '../../hooks/use_snackbar'

const Organization = () => {
  const { orgID } = useParams()

  const [org, setOrg] = useState({})

  const queryKey = ['organizations/', orgID]

  const { updateModel } = useStore()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { addErrorMessage } = useSnackbar()

  const { data: organization, isLoading } = useQuery(queryKey)
  const { data: { data: users } = { data: [] } } = useQuery(['users'])
  const { data: airlines } = useQuery(['airlines'])

  const update = (value, isLocal) => {
    if (isLocal) {
      setOrg({ ...org, ...value })
    } else {
      updateModel.mutate({
        endpoint: 'organizations',
        id: orgID,
        data: value,
      })
    }
  }

  useEffect(() => {
    setOrg(organization)
  }, [organization])

  return (
    <Page breadcrumbs={[{ text: 'Organizations', to: '/settings/organizations' }, { text: organization?.name }]}>
      <div className='flex flex-col space-y-4'>
        <Input
          value={org?.name}
          label='Name'
          onChange={(e) => update({ name: e.target.value }, true)}
          onBlur={() => update({ name: org?.name }, false)}
        />
        <Select
          label='Owner'
          value={
            org?.owner_id
              ? {
                  value: org.owner_id,
                  label: users?.find((opt) => opt.id === org.owner_id)?.name,
                }
              : null
          }
          isLoading={isLoading}
          options={users?.map((opt) => ({ value: opt.id, label: opt.name })) || []}
          onChange={(opt) => update({ owner_id: opt.value }, false)}
        />
        <Select
          label='Airlines'
          options={airlines?.map((item) => ({ value: item.id, label: item.name }))}
          isMulti
          value={org?.airlines?.map((a) => ({ value: a.id, label: a.name })) || []}
          onChange={(opts) => update({ airlines: opts.map((opt) => opt.value) }, false)}
        />
      </div>
      {user?.role_id === 1 && (
        <div className='flex flex-col space-y-4 py-4'>
          <FileUpload
            className={'h-40'}
            backgroundImage={{
              url: org?.logo_url,
            }}
            upload={{
              message: 'Drop image here',
              accept: {
                'image/*': ['.jpg', '.jpeg', '.png'],
              },
              icon: 'image',
              publicURL: `${import.meta.env.VITE_API_URL}/api/organizations/upload-logo/${orgID}`,
              onComplete: () => {
                queryClient.fetchQuery(queryKey).then((res) => setOrg(res))
                queryClient.invalidateQueries(['/user'])
              },
              onRemove: () => {
                updateModel.mutate({ endpoint: 'organizations', id: orgID, data: { logo_url: '' } })
                setOrg((prev) => ({ ...prev, logo_url: '' }))
                queryClient.invalidateQueries(['/user'])
              },
              onError: (err) => {
                addErrorMessage(err.message)
              },
            }}
            button={{
              text: 'Upload Logo',
            }}
          />
          <Toggle
            checked={org?.oidc_enabled}
            label={'Enable OpenID Connect for this organization'}
            onChange={() => update({ oidc_enabled: !org?.oidc_enabled }, false)}
          />
          <Input
            disabled={!org?.oidc_enabled}
            label={'Domain Name'}
            name='host_name'
            value={org?.host_name}
            onChange={(e) => update({ host_name: e.target.value }, true)}
            onBlur={() => update({ host_name: org?.host_name }, false)}
          />

          <Input
            disabled={!org?.oidc_enabled}
            label={'OIDC Issuer URL'}
            name='oidc_issuer_url'
            value={org?.oidc_issuer_url}
            onChange={(e) => update({ oidc_issuer_url: e.target.value }, true)}
            onBlur={() => update({ oidc_issuer_url: org?.oidc_issuer_url }, false)}
          />
          <Input
            disabled={!org?.oidc_enabled}
            label={'OIDC Client ID'}
            name='oidc_client_id'
            value={org?.oidc_client_id}
            onChange={(e) => update({ oidc_client_id: e.target.value }, true)}
            onBlur={() => update({ oidc_client_id: org?.oidc_client_id }, false)}
          />
          <Input
            disabled={!org?.oidc_enabled}
            label={'OIDC Client Secret'}
            name='oidc_client_secret'
            value={org?.oidc_client_secret}
            onChange={(e) => update({ oidc_client_secret: e.target.value }, true)}
            onBlur={() => update({ oidc_client_secret: org?.oidc_client_secret }, false)}
          />
        </div>
      )}
    </Page>
  )
}

export default Organization
